import db from "../db/database";
import { markForDelete, markForSync } from "./syncService";
import { componerApellidos } from "./userSyncService";

// Servicio para manejar las operaciones relacionadas con los usuarios

// Agregar un nuevo usuario a la base de datos
export const addUser = async (user) => {
  try {
    // La unicidad se comprueba sobre el No., que es la clave real: en BC el
    // cajero se identifica por Tienda + Id, y en el TPV es además con lo que se
    // inicia sesión.
    //
    // Antes se comprobaba el nombre de usuario y el correo. Ninguno de los dos
    // servía: el nombre de usuario no viajaba a BC y el pull lo sobreescribía
    // con el Nombre, y el correo está vacío en 19 de los 24 cajeros del
    // ambiente y repetido en tres de los que lo tienen -- una restricción sobre
    // ese campo bloqueaba altas legítimas por un dato que BC no considera
    // identificador.
    //
    // Un No. vacío es válido: significa que lo asigna BC al sincronizar.
    if (user.No) {
      const yaExiste = await db.users
        .where("No")
        .equals(user.No)
        .and((otro) => !otro.deleted && (otro.Tienda || "") === (user.Tienda || ""))
        .first();

      if (yaExiste) {
        throw new Error(`Ya existe un usuario con el No. ${user.No} en esta tienda`);
      }
    }

    // Agregar el usuario a la base de datos
    const id = await db.users.add({
      firstName: user.firstName,
      primerApellido: user.primerApellido || "",
      segundoApellido: user.segundoApellido || "",
      // Derivado de los dos anteriores: lo leen las pantallas y el buscador, pero
      // el origen de la verdad son los campos separados.
      lastName: componerApellidos(user.primerApellido, user.segundoApellido),
      email: user.email,
      password: user.password, // En una aplicación real, la contraseña debería ser hasheada
      createdAt: new Date(),
      pendingSync: 1, // Marcar como pendiente de sincronización (usando 1 en lugar de true)
      lastModified: new Date(),
      No: user.No || null, // Guardar el No de Business Central si existe
      deleted: false,
      Admin: user.Admin || false, // Nuevo campo Admin con valor por defecto false
      Tienda: user.Tienda || "",
      Grupo: user.Grupo || "",
    });

    return { id, ...user };
  } catch (error) {
    console.error("Error al agregar usuario:", error);
    throw error;
  }
};

// Obtener todos los usuarios
export const getAllUsers = async () => {
  try {
    // Obtener solo usuarios no marcados como eliminados
    const users = await db.users.filter((user) => !user.deleted).toArray();
    return users;
  } catch (error) {
    console.error("Error al obtener usuarios:", error);
    throw error;
  }
};

// Obtener un usuario por ID local
export const getUserById = async (id) => {
  try {
    const user = await db.users.get(id);
    if (!user || user.deleted) {
      throw new Error("Usuario no encontrado");
    }
    return user;
  } catch (error) {
    console.error(`Error al obtener usuario con ID ${id}:`, error);
    throw error;
  }
};

// Obtener un usuario por No de Business Central
export const getUserByNo = async (no) => {
  try {
    const user = await db.users
      .where("No")
      .equals(no)
      .and((user) => !user.deleted)
      .first();

    if (!user) {
      throw new Error("Usuario no encontrado");
    }
    return user;
  } catch (error) {
    console.error(`Error al obtener usuario con No ${no}:`, error);
    throw error;
  }
};

// Iniciar sesión de usuario
export const loginUser = async (username, password) => {
  try {
    const user = await db.users
      .where("No")
      .equals(username)
      .and((user) => !user.deleted) // Asegurar que no está eliminado
      .first();

    if (!user) {
      throw new Error("Usuario no encontrado");
    }

    if (user.password !== password) {
      // En una aplicación real, se debería comparar hashes
      throw new Error("Contraseña incorrecta");
    }

    return user;
  } catch (error) {
    console.error("Error al iniciar sesión:", error);
    throw error;
  }
};

// Resolver el registro real del usuario de la sesión en la base local.
//
// El id local es autoincremental y NO es estable entre recargas de maestros:
// al vaciar y volver a poblar la tabla users desde BC, los registros vuelven
// con ids nuevos, mientras la cookie de sesión sigue guardando el id viejo.
// El identificador estable es el No de BC (el mismo con el que se inicia
// sesión en loginUser), así que se busca primero por ahí y el id local queda
// solo como respaldo.
export const resolveUserRecord = async (sessionUserOrId) => {
  const sessionUser =
    sessionUserOrId && typeof sessionUserOrId === "object"
      ? sessionUserOrId
      : { id: sessionUserOrId };

  const findBy = async (field, value) => {
    if (value === undefined || value === null || value === "") return null;
    return await db.users
      .where(field)
      .equals(value)
      .and((user) => !user.deleted)
      .first();
  };

  const byNo = await findBy("No", sessionUser.No);
  if (byNo) return byNo;

  // Con `No` y sin registro, **no** se recurre al id local ni a los demás
  // respaldos. El id se recicla al recargar maestros, así que ese respaldo
  // podía devolver a OTRA persona con el id viejo de la sesión, y el terminal
  // seguía operando con un cajero que el maestro ya no tiene. Acuerdo
  // `contrato-hallazgos-ciclo-cri-2026-09-12.md`, H9.
  if (String(sessionUser.No ?? "").trim()) return null;

  if (sessionUser.id !== undefined && sessionUser.id !== null) {
    const byId = await db.users.get(sessionUser.id);
    if (byId && !byId.deleted) return byId;
  }

  const byUsername = await findBy("username", sessionUser.username);
  if (byUsername) return byUsername;

  return (await findBy("email", sessionUser.email)) || null;
};

/**
 * Si el usuario de la sesión guardada sigue existiendo en el maestro.
 *
 * La cookie dura 14 días y guarda al usuario tal como era al entrar. Si en ese
 * tiempo su `No` sale del maestro, el terminal seguía vendiendo con él, y el
 * `ID_Cajero` de cada venta llevaba un código que Business Central no conoce.
 * Medido en Costa Rica DEV el 12/09/2026: diez facturas con el cajero `SUPEXC`,
 * que no existe; el que existe es `SUPEXC15`.
 *
 * No se da por caducada cuando no hay con qué comprobarlo: si la sesión no trae
 * `No`, si el maestro local está vacío --todavía no se sincronizó, y sacar al
 * cajero lo dejaría sin poder entrar-- o si la base no responde. Acuerdo
 * `contrato-hallazgos-ciclo-cri-2026-09-12.md`, H9.
 *
 * @param {object} sessionUser El usuario guardado en la cookie.
 * @returns {Promise<boolean>}
 */
export const sesionSigueVigente = async (sessionUser) => {
  const no = String(sessionUser?.No ?? "").trim();
  if (!no) return true;

  try {
    if ((await db.users.count()) === 0) return true;

    const registro = await db.users
      .where("No")
      .equals(no)
      .and((user) => !user.deleted)
      .first();

    return Boolean(registro);
  } catch (error) {
    console.error("No se pudo comprobar el usuario de la sesión:", error);
    return true;
  }
};

// Cambiar contraseña de usuario
// Recibe el usuario de la sesión (o su id local, por compatibilidad).
export const changePassword = async (sessionUser, currentPassword, newPassword) => {
  try {
    const user = await resolveUserRecord(sessionUser);

    if (!user) {
      throw new Error("Usuario no encontrado");
    }

    // Verificar que la contraseña actual sea correcta
    if (user.password !== currentPassword) {
      throw new Error("La contraseña actual es incorrecta");
    }

    // Actualizar la contraseña
    await db.users.update(user.id, {
      password: newPassword,
      pendingSync: 1, // Usar 1 en lugar de true
      lastModified: new Date(),
    });

    // Marcar para sincronización
    await markForSync("users", user.id);

    // Devolver el registro actualizado para que la sesión se refresque con el
    // id local vigente y la contraseña nueva.
    return {
      success: true,
      user: { ...user, password: newPassword, pendingSync: 1 },
    };
  } catch (error) {
    console.error("Error al cambiar contraseña:", error);
    throw error;
  }
};

// Actualizar un usuario existente
export const updateUser = async (id, userData) => {
  try {
    const user = await db.users.get(id);
    if (!user || user.deleted) {
      throw new Error("Usuario no encontrado");
    }

    // Si no se proporciona una contraseña nueva, mantener la actual
    if (!userData.password) {
      userData.password = user.password;
    }

    // Actualizar el usuario. `lastName` se recalcula a partir de los dos
    // apellidos para que nunca quede desfasado del origen de la verdad.
    await db.users.update(id, {
      ...userData,
      lastName: componerApellidos(
        userData.primerApellido ?? user.primerApellido,
        userData.segundoApellido ?? user.segundoApellido
      ),
      lastModified: new Date(),
    });

    // Marcar como pendiente de sincronización
    await markForSync("users", id);

    return await getUserById(id);
  } catch (error) {
    console.error(`Error al actualizar usuario con ID ${id}:`, error);
    throw error;
  }
};

// Eliminar un usuario
export const deleteUser = async (id) => {
  try {
    const user = await db.users.get(id);
    if (!user) {
      throw new Error("Usuario no encontrado");
    }

    // Marcar como eliminado y pendiente de sincronización
    await markForDelete("users", id);

    return true;
  } catch (error) {
    console.error(`Error al eliminar usuario con ID ${id}:`, error);
    throw error;
  }
};
